// Common IANA timezones — full list pulled from Intl.supportedValuesOf at runtime if available
const FALLBACK_ZONES = [
  "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Vancouver", "America/Mexico_City", "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Rome", "Europe/Amsterdam", "Europe/Stockholm", "Europe/Moscow", "Europe/Istanbul",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi",
  "Asia/Dubai", "Asia/Riyadh", "Asia/Jerusalem", "Asia/Karachi",
  "Asia/Kolkata", "Asia/Dhaka", "Asia/Bangkok", "Asia/Jakarta", "Asia/Singapore",
  "Asia/Manila", "Asia/Kuala_Lumpur", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Taipei",
  "Asia/Seoul", "Asia/Tokyo",
  "Australia/Perth", "Australia/Sydney", "Australia/Melbourne",
  "Pacific/Auckland", "Pacific/Honolulu",
];

/**
 * Country code → IANA timezone(s) mapping.
 * Covers all countries in COUNTRIES list (countries.js).
 */
const COUNTRY_TIMEZONES = {
  US: [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  ],
  GB: ["Europe/London"],
  IN: ["Asia/Kolkata"],
  CA: [
    "America/Toronto", "America/Winnipeg", "America/Edmonton", "America/Vancouver",
    "America/Halifax", "America/St_Johns",
  ],
  AU: [
    "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
    "Australia/Adelaide", "Australia/Perth", "Australia/Darwin",
  ],
  DE: ["Europe/Berlin"],
  FR: ["Europe/Paris"],
  ES: ["Europe/Madrid", "Atlantic/Canary"],
  IT: ["Europe/Rome"],
  NL: ["Europe/Amsterdam"],
  BR: [
    "America/Sao_Paulo", "America/Fortaleza", "America/Recife",
    "America/Manaus", "America/Cuiaba", "America/Belem",
    "America/Porto_Velho", "America/Boa_Vista", "America/Rio_Branco",
  ],
  MX: [
    "America/Mexico_City", "America/Cancun", "America/Merida",
    "America/Chihuahua", "America/Mazatlan", "America/Hermosillo", "America/Tijuana",
  ],
  AR: ["America/Argentina/Buenos_Aires"],
  JP: ["Asia/Tokyo"],
  KR: ["Asia/Seoul"],
  CN: ["Asia/Shanghai"],
  SG: ["Asia/Singapore"],
  ID: ["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"],
  PH: ["Asia/Manila"],
  TH: ["Asia/Bangkok"],
  MY: ["Asia/Kuala_Lumpur", "Asia/Kuching"],
  VN: ["Asia/Ho_Chi_Minh"],
  AE: ["Asia/Dubai"],
  SA: ["Asia/Riyadh"],
  IL: ["Asia/Jerusalem"],
  TR: ["Europe/Istanbul"],
  EG: ["Africa/Cairo"],
  ZA: ["Africa/Johannesburg"],
  NG: ["Africa/Lagos"],
  KE: ["Africa/Nairobi"],
  RU: [
    "Europe/Kaliningrad", "Europe/Moscow", "Europe/Samara",
    "Asia/Yekaterinburg", "Asia/Omsk", "Asia/Novosibirsk",
    "Asia/Irkutsk", "Asia/Yakutsk", "Asia/Vladivostok",
    "Asia/Magadan", "Asia/Sakhalin", "Asia/Kamchatka",
  ],
  PL: ["Europe/Warsaw"],
  SE: ["Europe/Stockholm"],
  NO: ["Europe/Oslo"],
  PK: ["Asia/Karachi"],
  BD: ["Asia/Dhaka"],
};

export function listTimezones() {
  let zones = [];
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      zones = Intl.supportedValuesOf("timeZone");
    }
  } catch {
    /* fall through */
  }
  if (!zones || zones.length === 0) zones = FALLBACK_ZONES;
  // Ensure "UTC" is always present and first
  const set = new Set(zones);
  set.delete("UTC");
  return ["UTC", ...Array.from(set).sort()];
}

/**
 * Return the list of IANA timezones for a given country code.
 * Falls back to the full timezone list if the country is unknown.
 * Invalid timezone identifiers are filtered out.
 */
export function getTimezonesByCountry(countryCode) {
  const zones = COUNTRY_TIMEZONES[countryCode?.toUpperCase()] || listTimezones();
  return zones.filter((tz) => {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  });
}

/**
 * Return the UTC offset string for a timezone at the current instant.
 * Correctly reflects DST (e.g. returns "UTC -07:00" for America/Los_Angeles in summer).
 * Examples: "UTC +05:30", "UTC -08:00", "UTC +00:00"
 */
export function getTimezoneOffset(timezone) {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    }).formatToParts(now);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart) {
      const raw = tzPart.value; // e.g. "GMT+5:30", "GMT-8", "GMT"
      if (raw === "GMT") return "UTC +00:00";
      const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (match) {
        const sign = match[1];
        const hours = match[2].padStart(2, "0");
        const minutes = (match[3] || "00").padStart(2, "0");
        return `UTC ${sign}${hours}:${minutes}`;
      }
    }
  } catch {
    /* fall through */
  }
  return "UTC +00:00";
}

/**
 * Format a timezone for display with its UTC offset.
 * Example: "(UTC +05:30) Asia/Kolkata"
 */
export function formatTimezoneWithOffset(timezone) {
  if (!timezone) return "UTC";
  const offset = getTimezoneOffset(timezone);
  return `(${offset}) ${timezone}`;
}

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Detect country and timezone from the user's IP address via ipapi.co.
 * Falls back gracefully: country → "US", timezone → browser Intl.
 * Returns { country: "IN", timezone: "Asia/Kolkata" }
 */
export async function detectLocationFromIP() {
  const tzFallback = detectTimezone();
  try {
    const res = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(4000) });
    if (!res.ok) throw new Error("non-200");
    const data = await res.json();
    return {
      country: data.country_code || "US",
      timezone: data.timezone || tzFallback,
    };
  } catch {
    return { country: "US", timezone: tzFallback };
  }
}

export function formatInTz(dateStr, tz) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  try {
    return d.toLocaleString("en-US", {
      timeZone: tz || "UTC",
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return d.toUTCString();
  }
}

export function relativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
